<script setup>
import { computed, ref } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import { PhoneArrowUpRightIcon, ChatBubbleBottomCenterTextIcon, LanguageIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import { PopperTooltip } from '@goodvibes/kudoslink';
import RoleplayLayout from '@/Layouts/RoleplayLayout.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import Spinner from '@/Components/Spinner.vue';
import VoiceChatCard from '@/Components/VoiceChatCard.vue';
import {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
} from '@headlessui/vue'
import { useUrlSearchParams } from '@vueuse/core';
import { usePaywallModal } from '@/usePaywallModal';
import { useLanguagePreference } from '@/useLanguagePreference';
import { requestMicrophonePermission } from '@/useVoiceChat';
import ChatIntro from '@/Components/ChatIntro.vue';

const props = defineProps({
    simulation: { type: Object, required: true },
    humanReadableScenarioDescription: { type: String, required: false },
    lastlyPlayedSimulations: { type: Array, required: true },
    previousRuns: { type: Array, required: true },
    playlists: { type: Array, required: false },
    supportedLanguages: { type: Array, required: true },
});

const searchParams = useUrlSearchParams();

const page = usePage();
const authUser = computed(() => page.props.auth.user);

const { lang: preferredLanguage, setLang: setPreferredLanguage, findLang: findSupportedLanguage } = useLanguagePreference(props.supportedLanguages);

const isChangingLanguage = ref(false);
const selectedLanguage = computed({
    get() {
        return (searchParams.lang && findSupportedLanguage(searchParams.lang)?.rfc5646) ?? preferredLanguage.value;
    },
    set(val) {
        isChangingLanguage.value = true;

        setPreferredLanguage(val);

        router.get(route('simulations.show', { roleplayUuid: props.simulation.uuid, ...searchParams, lang: val }), undefined, {
            onFinish: () => isChangingLanguage.value = false
        });
    }
});

const selectedLanguageName = computed(() => {
    return findSupportedLanguage(selectedLanguage.value)?.name;
});

const payWallModal = usePaywallModal();

async function startChat(type = 'text') {
    // INF (infinity) is encoded as null so we explicitly check for null
    if (page.props.auth.user.master.credits !== null && page.props.auth.user.master.credits < 1) {
        payWallModal.open({
            userTier: page.props.auth.user.master.tier,
            currentSubscription: page.props.auth.user.master.subscription,
            hasBillingAccess: page.props.auth.user.isMaster,
            reason: 'roleplay',
            countryCode: page.props.location?.iso_code,
            referrer: page.props.referrer,
        });
        return;
    }

    // this is not so important,
    // the reason why we request the permission here too is that if user clicked the voice chat button
    // he already showed strong interest into voice chat, so we ask for permission
    // and then we don't have to ask him to click somewhere again (when he is on the voice chat page)
    // because browsers allow to ask permission only after user's action
    if (type === 'voice') {
        await requestMicrophonePermission();
    }

    router.post(route('simulationRuns.store'), {
        selectedSimulation: props.simulation.id,
        lang: selectedLanguage.value,
        ...(searchParams.package ? { package: searchParams.package } : {}), //determine if i am doing a package

        // if voice chat, explicitly set introMessage to null
        // so it is not generated during creating of the run
        // (but instead it will be generated [and spoken] when the impersonator picks up the voice chat)
        
        // if text chat, we omit the introMessage property and server will generate for us
        ...(type === 'voice' ? { introMessage: null } : {}),
        chatType: type
    });
}
</script>

<template>
    <RoleplayLayout :simulation="simulation" :humanReadableScenarioDescription="humanReadableScenarioDescription" :previousRuns="previousRuns" :playlists="playlists">
        <template #mobile-navbar-title>
            <!-- v-if="authUser && !authUser.isDemoUser" -->
            <div class="flex gap-x-2 items-center overflow-hidden">
                <img :src="simulation.thumbnail" class="rounded-full w-10 aspect-square object-cover" />
                <div>
                    <div class="text-sm text-nowrap whitespace-nowrap text-ellipsis overflow-hidden">{{ simulation.impersonator.name }}</div>
                    <div class="text-xs text-nowrap whitespace-nowrap text-ellipsis overflow-hidden text-muted">by @{{ simulation.author.companyName ? simulation.author.companyName : simulation.author.name }}</div>
                </div>
            </div>
        </template>        

        <!-- main content -->
        <div class="h-full max-h-dvh w-full flex flex-col items-center justify-center gap-y-24">
            <div class="w-full flex justify-center gap-12">
                <ChatIntro 
                    class="md:hidden"
                    :simulation="simulation" 
                    :humanReadableScenarioDescription="humanReadableScenarioDescription" 
                />
                <VoiceChatCard
                    class="hidden md:block"
                    :participant="{ avatar: simulation.thumbnail, name: simulation.impersonator.name, role: simulation.impersonator.role }" 
                    :isSpeaking="true"
                />
        
                <!-- <VoiceChatCard 
                    class="hidden sm:block"
                    :participant="{ avatar: authUser.avatar, name: authUser.name, role: simulation.participant.role }" 
                /> -->
            </div>

            <div class="flex items-center gap-x-6 [&>*]:shrink-0 [&>*]:grow-0">
                <Listbox v-model="selectedLanguage">
                    <div class="relative">
                        <ListboxButton class="relative overflow-hidden text-primary rounded-3xl text-sm " :class="{ 'glimmer-border': false }">
                            <div class="bg-white relative z-10 m-[2px] rounded-3xl px-6 py-3 whitespace-nowrap border-2" :class="[false ? 'border-primary/20' : 'border-primary']">
                                <Component :is="isChangingLanguage ? Spinner : LanguageIcon" class="inline-block mr-2 size-6 text-current" :class="{'animate-spin': isChangingLanguage}" />
                                <span class="inline-block">{{ selectedLanguageName }}</span>
                                <ChevronUpDownIcon class="ml-2 -mr-3 inline-block size-6 text-primary" aria-hidden="true"/>
                            </div>
                        </ListboxButton>
                        <ListboxOptions class="absolute bg-white shadow-lg rounded-xl max-h-64 overflow-y-auto px-6 py-3 mt-2">
                            <ListboxOption
                                v-slot="{ active, selected }"
                                v-for="language in props.supportedLanguages"
                                :key="language.rfc5646"
                                :value="language.rfc5646"
                            >
                                <div class="cursor-pointer py-1 my-1" :class="{ 'text-primary font-semibold': selected }">
                                    {{ language.name }}
                                </div>
                            </ListboxOption>
                        </ListboxOptions>
                    </div>
                </Listbox>

                <PopperTooltip placement="bottom" :triggers="['hover']" :delay="{ hide: 75, show: 200 }" :autoHide="true">
                    <SecondaryButton
                        class="block"
                        :disabled="isChangingLanguage"
                        @click="startChat('text')"
                    >
                        <template #icon>
                            <ChatBubbleBottomCenterTextIcon class="block size-6" />
                        </template>
                    </SecondaryButton>
                    <template #popper>
                        <p>Start chat</p>
                    </template>
                </PopperTooltip>

                <PopperTooltip placement="bottom" :triggers="['hover']" :delay="{ hide: 75, show: 200 }" :autoHide="true">
                    <PrimaryButton 
                        class="!bg-green-400 !ring-green-400 shake-animation" 
                        :disabled="isChangingLanguage"
                        @click="startChat('voice')"
                    >
                        <template #icon>
                            <PhoneArrowUpRightIcon class="block size-6" />
                        </template>
                    </PrimaryButton>
                    <template #popper>
                        <p>Start voice chat</p>
                    </template>
                </PopperTooltip>
            </div>
        </div>
    </RoleplayLayout>
</template>

<style>
.shake-animation {
    display: inline-block;
    animation: shake 0.75s ease-in-out 1s 2;
}

@keyframes shake {
    0%, 100% { transform: rotate(0deg) scale(1); }
    25% { transform: rotate(-5deg) scale(1.1); }
    50% { transform: rotate(0deg) scale(1.1); }
    75% { transform: rotate(5deg) scale(1.1); }
}

.glimmer-border::before {
    content: "";
    display: block;
    background: linear-gradient(90deg, rgba(98, 18, 229, 0) 0%, rgba(98, 18, 229, 0.75) 50%, rgba(98, 18, 229, 0) 100%);
    height: 300px;
    width: 100px;
    transform: translate(0);
    position: absolute;
    animation: rotate 5s linear forwards infinite;
    z-index: 0;
    top: 50%;
    transform-origin: top center;
}

@keyframes rotate {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
